
import { useState, useCallback } from "react";
import { CompanyDetails, CompanySearchResult } from "@/lib/api/companyRegister";
import { useCompanyRegister } from "@/hooks/useCompanyRegister";
import { isCompanyRegisterAvailable } from "./index";

export function useCompanySearch() {
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<CompanySearchResult[]>([]);
  
  const {
    searchCompany,
    getCompanyDetails,
    providers,
    preferences,
  } = useCompanyRegister();
  
  // Check if the company register is available
  const isAvailable = isCompanyRegisterAvailable(providers, preferences);
  
  // Get the default provider ID
  const defaultProviderId = preferences?.defaultProviderId || 
    (providers.length > 0 ? providers[0].id : "");
  
  // Search for companies
  const search = useCallback(async (query: string) => {
    if (query.length < 2 || !isAvailable) {
      setSearchResults([]);
      return [];
    }
    
    setIsSearching(true);
    try {
      console.log("Searching with provider:", defaultProviderId);
      const results = await searchCompany(query, defaultProviderId);
      const validResults = Array.isArray(results) ? results : [];
      setSearchResults(validResults);
      return validResults;
    } catch (error) {
      console.error("Error searching for companies:", error);
      setSearchResults([]);
      return [];
    } finally {
      setIsSearching(false);
    }
  }, [defaultProviderId, isAvailable, searchCompany]);
  
  // Get company details
  const getDetails = useCallback(async (result: CompanySearchResult): Promise<CompanyDetails | null> => {
    try {
      const providerId = result.source.toLowerCase();
      console.log("Getting details for company with ID:", result.id, "from provider:", providerId);
      return await getCompanyDetails(result.id, providerId);
    } catch (error) {
      console.error("Error getting company details:", error);
      return null;
    }
  }, [getCompanyDetails]);
  
  // Clear search results
  const clearResults = useCallback(() => {
    setSearchResults([]);
  }, []);
  
  return {
    isAvailable,
    isSearching,
    searchResults,
    search,
    getDetails,
    clearResults,
    defaultProviderId
  };
}
